/*
 *
 * Reading what the `CatalogPatch` engine wrote.
 *
 * The engine's own definitions live in
 * `packages/backend/src/catalogProcessors/CatalogPatchProcessor/types.ts`. The
 * frontend cannot import across that boundary, so the annotation keys and the
 * payload shapes are restated here — keep the two in step.
 *
 * Everything below parses defensively. The annotations are ours, but they reach
 * the browser as strings on an entity that may have been through a bad cycle,
 * and a malformed one has to render nothing rather than take an entity page
 * down with it.
 *
 */

import {
  CompoundEntityRef,
  DEFAULT_NAMESPACE,
  Entity,
  parseEntityRef,
  stringifyEntityRef
} from '@backstage/catalog-model'

/** Written on every entity at least one patch applied to. */
export const ANNOTATION_PATCHES = 'sennder.com/patches'

/**
 * Prefix of the flat, per-field companion annotations. The suffix is the
 * patched field path — `sennder.com/patched.spec.owner` — and the value is what
 * the authored file still says.
 */
export const ANNOTATION_PATCHED_PREFIX = 'sennder.com/patched.'

/** Written on the patch itself by the self-status pass, never on a target. */
export const ANNOTATION_PATCH_STATUS = 'sennder.com/patch-status'
export const ANNOTATION_PATCH_OBSERVED = 'sennder.com/patch-observed'
export const ANNOTATION_PATCH_TARGETS = 'sennder.com/patch-targets'
export const ANNOTATION_PATCH_APPLIED_COUNT = 'sennder.com/patch-applied-count'

export const CATALOG_PATCH_KIND = 'CatalogPatch'

/** The one non-exact target form: every entity in the catalog. */
export const GLOBAL_TARGET = '*'

/** Where the docs page explaining all of this is served. */
export const CATALOG_PATCHES_DOCS_PATH =
  '/docs/default/component/developer-portal/catalog-rules/catalog-patches'

export type PatchStatus =
  | 'pending'
  | 'applied'
  | 'overridden'
  | 'no-live-targets'
  | 'conflict'
  | 'dangling'

const PATCH_STATUSES: PatchStatus[] = [
  'pending',
  'applied',
  'overridden',
  'no-live-targets',
  'conflict',
  'dangling'
]

/**
 * One entry per applied op. `from` carries the value the authored file still
 * says, and is omitted when the field was absent. `overriddenBy` names the
 * patch that won the field, and is absent on winners.
 */
export type ProvenanceEntry = {
  patch: string
  /** `spec.<path>`, `metadata.<field>` or `annotations.<key>`. */
  field: string
  from?: string
  overriddenBy?: string
}

export type TargetStatus = {
  ref: string
  status: PatchStatus
  observed?: string
  from?: string
}

export type CatalogPatchDetails = {
  /** Exact target refs. Empty for a `"*"` patch. */
  targets: string[]
  global: boolean
  reason?: string
  upstream: string[]
  /** Pre-formatted, one line per `when` condition. */
  conditions: string[]
  /** Pre-formatted, one line per op. */
  ops: string[]
}

/**
 * Provenance records a patch's bare name, because names are what the engine
 * uses as identifiers. Every patch is ingested from `service-catalog-data`
 * through `sennderEntityDataParser`, which forces `metadata.namespace` to
 * `default`, so the namespace can be filled in here.
 */
export function patchEntityRef(name: string): CompoundEntityRef {
  return { kind: CATALOG_PATCH_KIND, namespace: DEFAULT_NAMESPACE, name }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return undefined
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function parseJsonArray(raw: string | undefined): Record<string, unknown>[] {
  if (!raw) {
    return []
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }

  return Array.isArray(parsed) ? parsed.filter(isRecord) : []
}

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined
}

function recordList(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? (value as unknown[]).filter(isRecord) : []
}

function stringList(value: unknown): string[] {
  if (typeof value === 'string') {
    return [value]
  }
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string')
  }
  return []
}

/** Every op recorded on the entity, losers of a field collision included. */
export function readProvenance(entity: Entity): ProvenanceEntry[] {
  return parseJsonArray(entity.metadata.annotations?.[ANNOTATION_PATCHES])
    .filter(entry => typeof entry.patch === 'string' && typeof entry.field === 'string')
    .map(entry => ({
      patch: entry.patch as string,
      field: entry.field as string,
      from: optionalString(entry.from),
      overriddenBy: optionalString(entry.overriddenBy)
    }))
}

/**
 * Only the patch that actually owns each field. Which patch outranked which is
 * devx bookkeeping, and the owning team has no use for it.
 */
export function readWinningProvenance(entity: Entity): ProvenanceEntry[] {
  return readProvenance(entity)
    .filter(entry => !entry.overriddenBy)
    .sort((a, b) => a.field.localeCompare(b.field))
}

export function isPatchedEntity(entity: Entity): boolean {
  return readWinningProvenance(entity).length > 0
}

export function readPatchStatus(entity: Entity): PatchStatus | undefined {
  const raw = entity.metadata.annotations?.[ANNOTATION_PATCH_STATUS]
  return PATCH_STATUSES.find(status => status === raw)
}

export function readPatchAppliedCount(entity: Entity): number | undefined {
  const raw = entity.metadata.annotations?.[ANNOTATION_PATCH_APPLIED_COUNT]
  if (raw === undefined) {
    return undefined
  }
  const count = Number(raw)
  return Number.isFinite(count) ? count : undefined
}

export function readTargetStatuses(entity: Entity): TargetStatus[] {
  return parseJsonArray(entity.metadata.annotations?.[ANNOTATION_PATCH_TARGETS])
    .map(entry => {
      const ref = optionalString(entry.ref)
      const status = PATCH_STATUSES.find(candidate => candidate === entry.status)
      if (!ref || !status) {
        return undefined
      }
      return {
        ref,
        status,
        observed: optionalString(entry.observed),
        from: optionalString(entry.from)
      }
    })
    .filter(isDefined)
}

function fieldOfCondition(condition: Record<string, unknown>): string | undefined {
  if (typeof condition.specField === 'string') {
    return `spec.${condition.specField}`
  }
  if (typeof condition.metadataField === 'string') {
    return `metadata.${condition.metadataField}`
  }
  if (typeof condition.annotation === 'string') {
    return `annotations.${condition.annotation}`
  }
  return undefined
}

function describeCondition(condition: Record<string, unknown>): string | undefined {
  const field = fieldOfCondition(condition)
  if (!field) {
    return undefined
  }
  if (condition.absent === true) {
    return `${field} is absent`
  }
  if (typeof condition.equals === 'string') {
    return `${field} equals ${condition.equals || '(empty)'}`
  }
  return undefined
}

function describeOp(op: Record<string, unknown>): string | undefined {
  const value = optionalString(op.value)

  switch (op.op) {
    case 'setSpecField':
      return typeof op.field === 'string' && value !== undefined
        ? `spec.${op.field} = ${value}`
        : undefined
    case 'setMetadataField':
      return typeof op.field === 'string' && value !== undefined
        ? `metadata.${op.field} = ${value}`
        : undefined
    case 'setAnnotation':
      return typeof op.key === 'string' && value !== undefined
        ? `annotations.${op.key} = ${value}`
        : undefined
    case 'unsetAnnotation':
      return typeof op.key === 'string' ? `annotations.${op.key} removed` : undefined
    default:
      return undefined
  }
}

/** The patch's own `spec`, flattened into what the patch page renders. */
export function readPatchDetails(entity: Entity): CatalogPatchDetails {
  const spec: Record<string, unknown> = entity.spec ?? {}
  // `"*"` is the literal string and nothing else — the schema rejects it inside
  // a list, so `['*']` would be an exact reference that resolves to nothing.
  const global = spec.target === GLOBAL_TARGET

  return {
    targets: global ? [] : stringList(spec.target),
    global,
    reason: optionalString(spec.reason),
    upstream: stringList(spec.upstream),
    conditions: recordList(spec.when).map(describeCondition).filter(isDefined),
    ops: recordList(spec.ops).map(describeOp).filter(isDefined)
  }
}

/**
 * Fields whose values are entity references, with the kind to fill in when the
 * authored value omits it. Restated from the engine's `REF_VALUED_SPEC_FIELDS`.
 */
const REF_VALUED_SPEC_FIELDS: Record<string, string> = {
  owner: 'group',
  system: 'system',
  domain: 'domain',
  subcomponentOf: 'component',
  parent: 'group'
}

function normaliseRef(field: string, value: string): string | undefined {
  try {
    return stringifyEntityRef(
      parseEntityRef(value, {
        defaultKind: REF_VALUED_SPEC_FIELDS[field],
        defaultNamespace: DEFAULT_NAMESPACE
      })
    )
  } catch {
    return undefined
  }
}

/**
 * The catalog filter that finds the entities a `"*"` patch is currently
 * correcting, and `undefined` for every other patch.
 *
 * This is the same lookup the status pass counts. A `"*"` patch is restricted
 * by the schema to one `specField ... equals` condition and one `setSpecField`
 * op on that field, so its live blast radius is exactly the entities carrying
 * the companion annotation with the old value.
 *
 * The companion carries the value the file *authored*, so the `when` value is
 * matched as written and, in case it was written short, in normalised form too.
 */
export function globalPatchFilter(entity: Entity): Record<string, string[]> | undefined {
  const spec: Record<string, unknown> = entity.spec ?? {}

  if (spec.target !== GLOBAL_TARGET) {
    return undefined
  }

  const [op] = recordList(spec.ops)
  const [condition] = recordList(spec.when)

  if (
    op?.op !== 'setSpecField' ||
    typeof op.field !== 'string' ||
    typeof condition?.specField !== 'string' ||
    typeof condition.equals !== 'string'
  ) {
    return undefined
  }

  const values = new Set([
    condition.equals,
    normaliseRef(op.field, condition.equals) ?? condition.equals
  ])

  return {
    [`metadata.annotations.${ANNOTATION_PATCHED_PREFIX}spec.${op.field}`]: [...values]
  }
}

function getAtPath(root: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((current, segment) => {
    return isRecord(current) ? current[segment] : undefined
  }, root)
}

/**
 * The value the portal currently serves for a provenance entry's field. Paired
 * with the entry's `from`, this is the whole disclosure: what we show, and what
 * the repository still says.
 */
export function readPatchedFieldValue(entity: Entity, field: string): string | undefined {
  if (field.startsWith('annotations.')) {
    return entity.metadata.annotations?.[field.slice('annotations.'.length)]
  }
  if (field.startsWith('metadata.')) {
    return asString(getAtPath(entity.metadata, field.slice('metadata.'.length)))
  }
  if (field.startsWith('spec.')) {
    return asString(getAtPath(entity.spec, field.slice('spec.'.length)))
  }
  return undefined
}
